import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Zap, Lock, CheckCircle2, ShieldCheck } from "lucide-react";

// P6L — "RAPTOR Fast — pronto per l'avvio" (P1.7.3, service_tier FAST).
// Un tap => una sola /start (lock sincrono useRef + disabilitazione DOM
// immediata). 202 = successo. Nessun retry, nessun fallback.
export const FastRunPanel = ({ a, jobId, onChanged }) => {
  const [checked, setChecked] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(null);
  const [startError, setStartError] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const lock = useRef(false);
  const btnRef = useRef(null);
  const cbRef = useRef(null);
  const inpRef = useRef(null);

  const PHRASE = a.confirmation_phrase || "";
  const ready = !!a.readiness?.ready;
  const phraseOk = PHRASE.length > 0 && phrase === PHRASE;
  const canStart = a.startable && ready && checked && phraseOk && !submitting && !started;
  const locked = submitting || !!started || !a.startable;
  const failing = Object.entries(a.readiness?.checks || {}).filter(([, v]) => !v).map(([k]) => k);
  const bf = a.budget_frozen || {};
  const runStarted = !!a.consumed && a.run_status && a.run_status !== "AUTHORIZED_AWAITING_OWNER_START";
  const historical = a.consumed || a.expired;

  const onStart = async (ev) => {
    ev?.preventDefault?.();
    if (lock.current) return;
    lock.current = true;
    [btnRef.current, cbRef.current, inpRef.current].forEach((el) => { if (el) el.disabled = true; });
    setSubmitting(true); setAttempted(true); setStartError(null);
    try {
      const res = await api.post(`/reader/jobs/${jobId}/segmented-run-fast/start`,
        { confirm: true, confirmation_text: phrase });
      if (res.status === 202 || res.status === 200) { setStarted(res.data || { accepted: true }); onChanged?.(); }
      else setStartError({ status: res.status, code: null, message: `Risposta inattesa HTTP ${res.status}: il run non risulta avviato.` });
    } catch (e) {
      setStartError(describeApiError(e)); onChanged?.();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="w-full min-w-0 border-2 border-sky-400 bg-sky-50/40 p-2 sm:p-3 space-y-3 break-anywhere" data-testid="fast-run-panel">
      <div className="space-y-0.5">
        <div className="font-bold text-sm sm:text-base flex items-center gap-2" data-testid="fast-run-title">
          <Zap size={16} className="text-sky-700 shrink-0" />
          {runStarted ? "RAPTOR Fast — run avviato" : (historical ? "RAPTOR Fast — stato storico" : "RAPTOR Fast — pronto per l'avvio")}
        </div>
        <div className="text-[11px] text-muted-foreground">
          Versione: <b className="text-foreground break-anywhere" data-testid="fast-run-version">{a.authorization_reader_version}</b>
          {" "}· service tier <b className="text-foreground" data-testid="fast-run-service-tier">{a.service_tier}</b>
        </div>
      </div>

      {!runStarted && (
        <div className={`text-[11px] px-2 py-1.5 border ${ready ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="fast-run-readiness">
          Readiness: <b>{ready ? "PRONTO" : "NON PRONTO"}</b>{" — "}{failing.length ? failing.join(", ") : "tutti i controlli superati"}
        </div>
      )}
      {runStarted && (
        <div className={`text-[11px] px-2 py-1.5 border ${a.incomplete_reason ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}`} data-testid="fast-run-readiness">
          <b>Run avviato</b> · stato run: <b>{a.run_status}</b>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]" data-testid="fast-run-facts">
        <div className="min-w-0">Run: <span className="font-mono text-[10px] break-anywhere" data-testid="fast-run-id">{a.logical_reader_run_id}</span></div>
        <div>Stato: <b data-testid="fast-run-state">{a.state}</b></div>
        <div>Costo stimato Fast: <b data-testid="fast-run-typical">${bf.typical_total_usd}</b> (worst ${bf.worst_case_total_usd} · min. tetto ${bf.minimum_required_cap_usd})</div>
        <div>Tetto assoluto: <b data-testid="fast-run-cap">${a.max_cost_usd}</b></div>
        <div>Max POST: <b data-testid="fast-run-max-posts">{a.max_calls}</b> (una per segmento) · retry: <b>nessuno</b></div>
        <div>Esito obbligatorio: <b data-testid="fast-run-final-state">{a.final_state_forced}</b></div>
        <div>Scadenza autorizzazione: <b data-testid="fast-run-expiry">{(a.expires_at || "").slice(0, 16)}</b></div>
        <div>Polling: max {a.poll_limits?.max_gets} GET / {Math.round((a.poll_limits?.max_seconds || 0) / 60)} min per segmento</div>
      </div>
      {a.incomplete_reason && (
        <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900 break-anywhere" data-testid="fast-run-incomplete-reason">
          Motivo blocco: {a.incomplete_reason}
        </div>
      )}

      <div className="table-scroll border border-border" data-testid="fast-run-segments">
        <table className="w-full text-[10px]">
          <thead><tr className="border-b border-border bg-muted text-left">
            {["Seg", "Stato", "Tipo", "Codice", "Img", "worst Fast $"].map((h) =>
              <th key={h} className="px-1.5 py-1 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {(a.segments || []).map((s) => (
              <tr key={s.segment_id} className="border-b border-border">
                <td className="px-1.5 py-1 font-bold">{s.segment_id}</td>
                <td className="px-1.5 py-1 whitespace-nowrap" data-testid={`fast-seg-state-${s.segment_id}`}>{s.state || "—"}</td>
                <td className="px-1.5 py-1 whitespace-nowrap">{s.kind}</td>
                <td className="px-1.5 py-1 whitespace-nowrap">{s.component_code || "—"}</td>
                <td className="px-1.5 py-1">{s.n_images}</td>
                <td className="px-1.5 py-1">{s.worst_case_usd ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {started && (
        <div className="flex items-start gap-1.5 text-[11px] border border-emerald-400 bg-emerald-50 p-2 text-emerald-900" data-testid="fast-start-accepted">
          <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
          <span>Avvio accettato (HTTP 202): run <span className="font-mono">{started.logical_reader_run_id || a.logical_reader_run_id}</span> accodato sul worker Fast. Autorizzazione consumata: nessun secondo avvio.</span>
        </div>
      )}
      {startError && (
        <div className="text-[11px] border border-red-400 bg-red-50 p-2 text-red-900 break-anywhere space-y-0.5" data-testid="fast-start-error">
          <div className="font-bold">Avvio non eseguito{startError.status ? ` — HTTP ${startError.status}` : ""}{startError.code ? ` · ${startError.code}` : ""}</div>
          <div>{startError.message}</div>
          <div className="text-[10px]">Nessun nuovo tentativo automatico: l'esito richiede una decisione Owner.</div>
        </div>
      )}

      {a.startable && !started && !attempted && (
        <div className="border-t border-sky-300 pt-2 space-y-2 safe-pb">
          <label className="flex items-start gap-2 text-[11px]">
            <input type="checkbox" ref={cbRef} checked={checked} disabled={locked}
                   onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0"
                   data-testid="fast-owner-checkbox" />
            <span>Confermo come Owner l'avvio del run RAPTOR Fast (service tier FAST, una POST per segmento, max {a.max_calls} POST, tetto assoluto ${a.max_cost_usd}, nessun retry, esito {a.final_state_forced}).</span>
          </label>
          <input type="text" ref={inpRef} value={phrase} disabled={locked}
                 onChange={(e) => setPhrase(e.target.value)}
                 placeholder={`Digita esattamente: ${PHRASE}`}
                 className="w-full min-h-[44px] border border-border px-2 py-2 text-base font-mono bg-white"
                 data-testid="fast-confirmation-input" />
          <Button ref={btnRef} onClick={onStart} disabled={!canStart} data-testid="fast-start-button"
                  className="w-full min-h-[44px] bg-sky-700 hover:bg-sky-800">
            {canStart ? <ShieldCheck size={14} className="mr-1" /> : <Lock size={14} className="mr-1" />}
            {submitting ? "Avvio in corso…" : "Avvia RAPTOR Fast"}
          </Button>
        </div>
      )}
      {historical && !started && !attempted && !runStarted && (
        <div className="text-[10px] text-muted-foreground" data-testid="fast-run-historical">
          Autorizzazione {a.consumed ? "consumata" : "scaduta"}: stato storico.
        </div>
      )}
    </div>
  );
};
